// Product editor: the form draft (strings, as typed), validation that mirrors the contract
// (docs/CATALOG-ADMIN-CONTRACT.md "Validation" + v1/fresh.js validateFreshSetup), the wire
// payload per line, and mapping a server 400 `field` back onto the form. Pure: no DOM, no Intl.

import { bandEstimateGrams, priceForGrams } from '@popcart/core/v1';
import { MAX_PPK_CENTAVOS, centavosToInput, parsePesos } from './money.js';
import { packError, packPartsOf, packPayload, variantLabelError } from './mart.js';

/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */
/** @typedef {import('@popcart/core/v1').ProductInput} ProductInput */
/** @typedef {import('@popcart/core/v1').AdminProductDetail} AdminProductDetail */
/** @typedef {import('@popcart/core/v1').CategoryView} CategoryView */
/** @typedef {'unit' | 'weight' | 'made_to_order'} Pricing */

/**
 * @typedef {object} BandDraft
 * @property {string} key  local list key
 * @property {string} minG
 * @property {string} maxG
 * @property {boolean} isDefault
 */
/**
 * @typedef {object} PrepDraft
 * @property {string} key
 * @property {string} code  '' until saved (generated from the label)
 * @property {string} label
 * @property {string} fee   pesos
 */
/**
 * @typedef {object} OptionDraft
 * @property {string} key
 * @property {string} code
 * @property {string} label
 * @property {string} price pesos
 * @property {boolean} isDefault
 */
/**
 * @typedef {object} GroupDraft
 * @property {string} key
 * @property {string} code
 * @property {string} name
 * @property {string} min
 * @property {string} max
 * @property {OptionDraft[]} options
 */
/**
 * @typedef {object} ComboDraft
 * @property {string} productId
 * @property {string} name
 * @property {string} qty
 */
/**
 * @typedef {object} ProductDraft
 * @property {string} name
 * @property {ProductLine} line
 * @property {Pricing} pricing
 * @property {string} price           pesos (unit / made_to_order)
 * @property {string} unitLabel
 * @property {string} categoryId      '' = none
 * @property {string} description
 * @property {string} source
 * @property {string} sort
 * @property {boolean} visible        create only (edits use the visibility switch)
 * @property {string} initialStock    create only: pieces, or kg for gram stock
 * @property {string} prepMinutes     grill
 * @property {string} menuSection     grill
 * @property {GroupDraft[]} groups    grill
 * @property {ComboDraft[]} combo     grill
 * @property {string} barcode         mart
 * @property {string} shelf           mart
 * @property {string} variantOf       mart: the base product's id when this is a variant ('' = none)
 * @property {string} variantLabel    mart: "1 L", "Sweet"
 * @property {import('./mart.js').PackDraft[]} pack  mart: value-pack parts ([] = not a pack)
 * @property {string} pricePerKg      fresh weight, pesos
 * @property {'piece' | 'gram'} stockUnit
 * @property {'bands' | 'target'} sizeMode
 * @property {BandDraft[]} bands
 * @property {string} targetMinG
 * @property {string} targetMaxG
 * @property {string} targetStepG
 * @property {PrepDraft[]} prepOptions
 */

/** Which pricing each line allows (contract: fresh unit|weight · grill made_to_order · mart unit). */
export const PRICING_BY_LINE = { fresh: ['unit', 'weight'], grill: ['made_to_order'], mart: ['unit'] };

/** @param {ProductLine} line @returns {Pricing} */
export function defaultPricing(line) {
  return /** @type {Pricing} */ (PRICING_BY_LINE[line][0]);
}

export const LIMITS = {
  name: 80,
  unitLabel: 24,
  description: 500,
  source: 80,
  barcode: 32,
  shelf: 20,
  menuSection: 40,
  prepMinMinutes: 1,
  prepMaxMinutes: 180,
  sort: 9999,
  maxBands: 10,
  maxPrep: 10,
  prepLabel: 40,
  prepFee: 100000, // ₱1,000 (v1/fresh.js)
  maxGrams: 100000, // 100 kg (v1/fresh.js MAX_G)
  maxGroups: 10,
  maxOptions: 20,
  groupName: 40,
  optionLabel: 40,
  optionPrice: 500000, // ₱5,000
  maxCombo: 10,
  comboQty: 20,
  stockPieces: 100000,
  stockGrams: 1000000, // 1,000 kg
};

const CONTROL_RE = /[\u0000-\u001f\u007f]/;

let seq = 0;
/** A local key for list rows (never sent). */
export function newKey() {
  seq += 1;
  return `k${seq}`;
}

/**
 * A blank draft for a new product.
 * @param {ProductLine} line
 * @returns {ProductDraft}
 */
export function emptyDraft(line) {
  return {
    name: '',
    line,
    pricing: defaultPricing(line),
    price: '',
    unitLabel: line === 'grill' ? '' : 'pc',
    categoryId: '',
    description: '',
    source: '',
    sort: '0',
    visible: true,
    initialStock: '',
    prepMinutes: '15',
    menuSection: '',
    groups: [],
    combo: [],
    barcode: '',
    shelf: '',
    variantOf: '',
    variantLabel: '',
    pack: [],
    pricePerKg: '',
    stockUnit: 'piece',
    sizeMode: 'bands',
    bands: [],
    targetMinG: '',
    targetMaxG: '',
    targetStepG: '',
    prepOptions: [],
  };
}

/**
 * Switch the line of a new product, keeping what still applies.
 * @param {ProductDraft} d @param {ProductLine} line
 * @returns {ProductDraft}
 */
export function changeLine(d, line) {
  if (d.line === line) return d;
  const blank = emptyDraft(line);
  return {
    ...blank,
    name: d.name,
    price: d.price,
    description: d.description,
    source: d.source,
    sort: d.sort,
    visible: d.visible,
    initialStock: d.initialStock,
  };
}

/**
 * Switch Fresh pricing; weight defaults the stock unit (bands count pieces, target weights grams).
 * @param {ProductDraft} d @param {'unit' | 'weight'} pricing
 * @returns {ProductDraft}
 */
export function changeFreshPricing(d, pricing) {
  if (pricing === 'unit') return { ...d, pricing, stockUnit: 'piece' };
  const bands = d.bands.length ? d.bands : [{ key: newKey(), minG: '', maxG: '', isDefault: true }];
  return { ...d, pricing, bands, stockUnit: d.sizeMode === 'target' ? 'gram' : 'piece' };
}

/** @param {number | null | undefined} n */
const str = (n) => (n === null || n === undefined ? '' : String(n));

/**
 * The form state for an existing product.
 * @param {AdminProductDetail} p
 * @returns {ProductDraft}
 */
export function draftFromDetail(p) {
  const d = emptyDraft(p.line);
  d.name = p.name;
  d.pricing = p.pricing;
  d.price = p.pricing === 'weight' ? '' : centavosToInput(p.priceCentavos);
  d.unitLabel = p.unitLabel ?? '';
  d.categoryId = p.categoryId ?? '';
  d.description = p.description ?? '';
  d.source = p.source ?? '';
  d.sort = String(p.sort ?? 0);
  d.visible = p.status === 'active';
  d.initialStock = '';
  d.stockUnit = p.stockUnit;
  if (p.line === 'grill') {
    d.prepMinutes = str(p.prepMinutes);
    d.menuSection = p.grill?.menuSection ?? '';
    d.groups = (p.grill?.modifierGroups ?? []).map((g) => ({
      key: newKey(),
      code: g.code,
      name: g.name,
      min: String(g.minChoices),
      max: String(g.maxChoices),
      options: g.options.map((o) => ({ key: newKey(), code: o.code, label: o.label, price: centavosToInput(o.priceCentavos), isDefault: !!o.isDefault })),
    }));
    d.combo = (p.grill?.comboComponents ?? []).map((c) => ({ productId: c.productId, name: c.name, qty: String(c.qty) }));
  }
  if (p.line === 'mart') {
    d.barcode = p.barcode ?? '';
    d.shelf = p.shelf ?? '';
    const m = /** @type {import('./mart.js').MartAdminDetail} */ (p);
    d.variantOf = m.variantOf ?? '';
    d.variantLabel = m.variantLabel ?? '';
    d.pack = packPartsOf(m);
  }
  if (p.line === 'fresh') {
    const f = p.fresh;
    const w = f?.weight ?? null;
    const ppk = f?.pricePerKgCentavos ?? w?.pricePerKgCentavos ?? p.pricePerKgCentavos;
    d.pricePerKg = ppk ? centavosToInput(ppk) : '';
    d.stockUnit = f?.stockUnit ?? p.stockUnit;
    const bands = f?.bands?.length ? f.bands : (w?.bands ?? []);
    const tMin = f?.targetMinG ?? w?.targetMinG ?? null;
    d.sizeMode = !bands.length && tMin ? 'target' : 'bands';
    d.bands = bands.map((b) => ({ key: newKey(), minG: String(b.minG), maxG: String(b.maxG), isDefault: !!b.isDefault }));
    d.targetMinG = str(tMin);
    d.targetMaxG = str(f?.targetMaxG ?? w?.targetMaxG);
    d.targetStepG = str(f?.targetStepG ?? w?.targetStepG);
    d.prepOptions = (f?.prepOptions ?? []).map((o) => ({ key: newKey(), code: o.code, label: o.label, fee: centavosToInput(o.feeCentavos) }));
  }
  return d;
}

// ── Codes ─────────────────────────────────────────────────────────────────────────────────

/**
 * A stable code from a label, unique among `taken`. Prep codes are a–z and _ only
 * (v1/fresh.js PREP_CODE_RE); modifier codes also allow digits.
 * @param {string} label @param {Set<string> | string[]} taken @param {{ digits?: boolean }} [o]
 */
export function makeCode(label, taken, o = {}) {
  const used = taken instanceof Set ? taken : new Set(taken);
  const allowed = o.digits ? /[^a-z0-9]+/g : /[^a-z]+/g;
  let base = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(allowed, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 20)
    .replace(/_+$/g, '');
  if (base.length < 2) base = (base + 'opt').slice(0, 3);
  if (!used.has(base)) return base;
  const letters = 'bcdefghijklmnopqrstuvwxyz';
  for (const ch of letters) {
    const c = `${base}_${ch}`;
    if (!used.has(c)) return c;
  }
  for (const a of letters) for (const b of letters) if (!used.has(`${base}_${a}${b}`)) return `${base}_${a}${b}`;
  return `${base}_${used.size}`;
}

// ── Number fields ─────────────────────────────────────────────────────────────────────────

/** @param {string} s */
const intOf = (s) => (/^\s*-?\d+\s*$/.test(s) ? Number(s.trim()) : NaN);

/**
 * Opening stock as typed → units on the wire: pieces, or grams when typed in kg.
 * @param {string} s @param {'piece' | 'gram'} unit
 * @returns {{ ok: true; value: number } | { ok: false; error: string }}
 */
export function parseStockAmount(s, unit) {
  const t = (s || '').trim().replace(/,/g, '');
  if (!t) return { ok: true, value: 0 };
  if (unit === 'gram') {
    const m = /^(\d+)(?:\.(\d{0,3}))?$/.exec(t);
    if (!m) return { ok: false, error: 'Enter kilograms, like 12.5.' };
    const g = Number(m[1]) * 1000 + Number((m[2] || '').padEnd(3, '0'));
    if (g > LIMITS.stockGrams) return { ok: false, error: 'That is more than 1,000 kg. Check the amount.' };
    return { ok: true, value: g };
  }
  if (!/^\d+$/.test(t)) return { ok: false, error: 'Enter a whole number of pieces.' };
  const n = Number(t);
  if (n > LIMITS.stockPieces) return { ok: false, error: 'That is more than 100,000. Check the amount.' };
  return { ok: true, value: n };
}

// ── Validation ────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {object} ValidateCtx
 * @property {readonly string[]} lines          the shop's product lines
 * @property {boolean} isCreate
 * @property {CategoryView[] | null} [categories] active categories of the draft's line (null = not loaded)
 * @property {string | null} [originalCategoryId] keeps an existing (now inactive) category valid
 * @property {string | null} [selfId]            the product being edited (a combo can't hold itself)
 */

/**
 * Check the whole form. Field keys match the form's field ids (see FIELD_IDS).
 * @param {ProductDraft} d @param {ValidateCtx} ctx
 * @returns {Record<string, string>} empty when valid
 */
export function validateDraft(d, ctx) {
  /** @type {Record<string, string>} */
  const e = {};
  const name = d.name.trim();
  if (!name) e.name = 'Give the product a name.';
  else if (name.length > LIMITS.name) e.name = `Keep the name to ${LIMITS.name} characters.`;
  else if (CONTROL_RE.test(name)) e.name = 'Remove the special characters from the name.';

  if (!ctx.lines.includes(d.line)) e.line = 'This shop doesn’t sell that line.';
  const pricings = /** @type {Record<string, string[]>} */ (PRICING_BY_LINE)[d.line] || [];
  if (!pricings.includes(d.pricing)) e.pricing = 'Choose how this product is priced.';

  if (d.pricing !== 'weight') {
    const p = parsePesos(d.price);
    if (!p.ok) e.price = p.error;
    else if (p.centavos <= 0) e.price = 'The price must be more than ₱0.';
  }

  if (d.unitLabel.trim().length > LIMITS.unitLabel) e.unitLabel = `Keep the unit to ${LIMITS.unitLabel} characters.`;
  if (d.description.trim().length > LIMITS.description) e.description = `Keep the description to ${LIMITS.description} characters.`;
  if (d.source.trim().length > LIMITS.source) e.source = `Keep the source to ${LIMITS.source} characters.`;

  const sort = d.sort.trim() === '' ? 0 : intOf(d.sort);
  if (!Number.isInteger(sort) || Math.abs(sort) > LIMITS.sort) e.sort = 'Sort is a whole number (lower shows first).';

  if (d.categoryId && ctx.categories) {
    const ok = ctx.categories.some((c) => c.id === d.categoryId && c.isActive && c.line === d.line);
    if (!ok && d.categoryId !== ctx.originalCategoryId) e.categoryId = 'Pick a category from the list.';
  }

  if (ctx.isCreate) {
    const s = parseStockAmount(d.initialStock, stockUnitOf(d));
    if (!s.ok) e.initialStock = s.error;
  }

  if (d.line === 'grill') Object.assign(e, validateGrill(d, ctx));
  if (d.line === 'mart') {
    const bc = d.barcode.trim();
    if (bc.length > LIMITS.barcode) e.barcode = `Keep the barcode to ${LIMITS.barcode} characters.`;
    else if (bc && !/^[A-Za-z0-9-]+$/.test(bc)) e.barcode = 'Barcodes are letters, numbers and dashes.';
    if (d.shelf.trim().length > LIMITS.shelf) e.shelf = `Keep the shelf to ${LIMITS.shelf} characters.`;
    const vl = variantLabelError(d.variantLabel, { required: !!d.variantOf });
    if (vl) e.variantLabel = vl;
    const pk = packError(d.pack, ctx.selfId ?? null);
    if (pk) e.pack = pk;
    else if (d.pack.length && d.variantOf) e.pack = 'A variant can’t be a value pack.';
  }
  if (d.line === 'fresh') Object.assign(e, validateFresh(d));
  return e;
}

/** The stock unit the product will have once saved. @param {ProductDraft} d @returns {'piece' | 'gram'} */
export function stockUnitOf(d) {
  if (d.line === 'fresh' && d.pricing === 'weight') return d.stockUnit;
  return 'piece';
}

/** @param {ProductDraft} d */
function validateFresh(d) {
  /** @type {Record<string, string>} */
  const e = {};
  if (d.pricing === 'weight') {
    const p = parsePesos(d.pricePerKg, { max: MAX_PPK_CENTAVOS, label: 'the price per kg' });
    if (!p.ok) e.pricePerKg = p.error;
    else if (p.centavos <= 0) e.pricePerKg = 'The price per kg must be more than ₱0.';
    if (d.stockUnit !== 'piece' && d.stockUnit !== 'gram') e.stockUnit = 'Count stock in pieces or by weight.';
    if (d.sizeMode === 'bands') {
      if (!d.bands.length) e.bands = 'Add at least one size band.';
      else if (d.bands.length > LIMITS.maxBands) e.bands = `Up to ${LIMITS.maxBands} size bands.`;
      else {
        const seen = new Set();
        for (const [i, b] of d.bands.entries()) {
          const min = intOf(b.minG);
          const max = intOf(b.maxG);
          if (!gramsOk(min) || !gramsOk(max)) {
            e.bands = `Band ${i + 1}: enter both weights in whole grams (1 to 100,000).`;
            break;
          }
          if (max < min) {
            e.bands = `Band ${i + 1}: the larger weight must be at least the smaller one.`;
            break;
          }
          const k = `${min}-${max}`;
          if (seen.has(k)) {
            e.bands = `Band ${i + 1} repeats another band.`;
            break;
          }
          seen.add(k);
        }
        if (!e.bands && d.bands.filter((b) => b.isDefault).length > 1) e.bands = 'Only one band can be the default.';
      }
    } else {
      const min = intOf(d.targetMinG);
      const max = intOf(d.targetMaxG);
      const step = intOf(d.targetStepG);
      if (!gramsOk(min) || !gramsOk(max) || !gramsOk(step)) e.target = 'Enter the smallest, largest and step in whole grams.';
      else if (max < min) e.target = 'The largest weight must be at least the smallest.';
      else if ((max - min) % step !== 0) e.target = `From ${min} g to ${max} g must go in whole steps of ${step} g.`;
    }
  }
  if (d.prepOptions.length > LIMITS.maxPrep) e.prepOptions = `Up to ${LIMITS.maxPrep} prep options.`;
  else {
    const labels = new Set();
    for (const [i, o] of d.prepOptions.entries()) {
      const label = o.label.trim();
      if (!label || label.length > LIMITS.prepLabel || CONTROL_RE.test(label)) {
        e.prepOptions = `Prep option ${i + 1}: a name of 1 to ${LIMITS.prepLabel} characters.`;
        break;
      }
      if (labels.has(label.toLowerCase())) {
        e.prepOptions = `“${label}” is listed twice.`;
        break;
      }
      labels.add(label.toLowerCase());
      const fee = o.fee.trim() === '' ? null : parsePesos(o.fee, { max: LIMITS.prepFee, label: 'a fee' });
      if (fee && !fee.ok) {
        e.prepOptions = `${label}: ${fee.error}`;
        break;
      }
    }
  }
  return e;
}

/** @param {number} g */
const gramsOk = (g) => Number.isInteger(g) && g >= 1 && g <= LIMITS.maxGrams;

/** @param {ProductDraft} d @param {ValidateCtx} ctx */
function validateGrill(d, ctx) {
  /** @type {Record<string, string>} */
  const e = {};
  const pm = intOf(d.prepMinutes);
  const lo = LIMITS.prepMinMinutes;
  const hi = LIMITS.prepMaxMinutes;
  if (!Number.isInteger(pm) || pm < lo || pm > hi) e.prepMinutes = `Prep time is ${lo} to ${hi} minutes.`;
  if (d.menuSection.trim().length > LIMITS.menuSection) e.menuSection = `Keep the menu section to ${LIMITS.menuSection} characters.`;

  if (d.groups.length > LIMITS.maxGroups) e.groups = `Up to ${LIMITS.maxGroups} choice groups.`;
  else {
    const names = new Set();
    for (const [gi, g] of d.groups.entries()) {
      const gname = g.name.trim();
      const tag = gname ? `“${gname}”` : `Group ${gi + 1}`;
      if (!gname || gname.length > LIMITS.groupName || CONTROL_RE.test(gname)) {
        e.groups = `Group ${gi + 1}: a name of 1 to ${LIMITS.groupName} characters (like “Sauce”).`;
        break;
      }
      if (names.has(gname.toLowerCase())) {
        e.groups = `${tag} is listed twice.`;
        break;
      }
      names.add(gname.toLowerCase());
      if (!g.options.length) {
        e.groups = `${tag}: add at least one option.`;
        break;
      }
      if (g.options.length > LIMITS.maxOptions) {
        e.groups = `${tag}: up to ${LIMITS.maxOptions} options.`;
        break;
      }
      const min = intOf(g.min);
      const max = intOf(g.max);
      if (!Number.isInteger(min) || min < 0) {
        e.groups = `${tag}: the minimum is 0 (optional) or more.`;
        break;
      }
      if (!Number.isInteger(max) || max < 1) {
        e.groups = `${tag}: customers must be able to pick at least 1.`;
        break;
      }
      if (min > max) {
        e.groups = `${tag}: the minimum can’t be more than the maximum.`;
        break;
      }
      if (max > g.options.length) {
        e.groups = `${tag}: the maximum (${max}) is more than its ${g.options.length} option${g.options.length === 1 ? '' : 's'}.`;
        break;
      }
      const labels = new Set();
      let bad = '';
      for (const [oi, o] of g.options.entries()) {
        const label = o.label.trim();
        if (!label || label.length > LIMITS.optionLabel || CONTROL_RE.test(label)) {
          bad = `${tag}, option ${oi + 1}: a name of 1 to ${LIMITS.optionLabel} characters.`;
          break;
        }
        if (labels.has(label.toLowerCase())) {
          bad = `${tag}: “${label}” is listed twice.`;
          break;
        }
        labels.add(label.toLowerCase());
        const pr = o.price.trim() === '' ? { ok: true } : parsePesos(o.price, { max: LIMITS.optionPrice, label: 'a price' });
        if (!pr.ok) {
          bad = `${tag}, ${label}: ${'error' in pr ? pr.error : ''}`;
          break;
        }
      }
      if (bad) {
        e.groups = bad;
        break;
      }
      if (g.options.filter((o) => o.isDefault).length > max) {
        e.groups = `${tag}: more options are pre-picked than customers can choose (${max}).`;
        break;
      }
    }
  }

  if (d.combo.length > LIMITS.maxCombo) e.combo = `Up to ${LIMITS.maxCombo} items in a combo.`;
  else {
    const ids = new Set();
    for (const c of d.combo) {
      if (ctx.selfId && c.productId === ctx.selfId) {
        e.combo = 'A combo can’t contain itself.';
        break;
      }
      if (ids.has(c.productId)) {
        e.combo = `${c.name} is in the combo twice. Raise its quantity instead.`;
        break;
      }
      ids.add(c.productId);
      const q = intOf(c.qty);
      if (!Number.isInteger(q) || q < 1 || q > LIMITS.comboQty) {
        e.combo = `${c.name}: quantity is 1 to ${LIMITS.comboQty}.`;
        break;
      }
    }
    if (!e.combo && d.combo.length === 1 && intOf(d.combo[0]?.qty ?? '') === 1) e.combo = 'A combo needs more than one item. Add another, or raise the quantity.';
  }
  return e;
}

// ── Payloads ──────────────────────────────────────────────────────────────────────────────

/** '' → null, trimmed otherwise. @param {string} s */
const opt = (s) => {
  const t = s.trim();
  return t ? t : null;
};
/** @param {string} s */
const pesosOr0 = (s) => {
  const p = s.trim() === '' ? null : parsePesos(s);
  return p && p.ok ? p.centavos : 0;
};

/**
 * The line setup sent inline. Codes are kept for existing rows and generated for new ones.
 * @param {ProductDraft} d
 * @returns {import('@popcart/core/v1').FreshProductSetup}
 */
export function buildFreshSetup(d) {
  const taken = new Set(d.prepOptions.map((o) => o.code).filter(Boolean));
  const prepOptions = d.prepOptions.map((o) => {
    const code = o.code || makeCode(o.label, taken);
    taken.add(code);
    return { code, label: o.label.trim(), feeCentavos: pesosOr0(o.fee) };
  });
  if (d.pricing !== 'weight') return { pricing: 'unit', stockUnit: 'piece', prepOptions };
  const ppk = parsePesos(d.pricePerKg, { max: MAX_PPK_CENTAVOS });
  /** @type {import('@popcart/core/v1').FreshProductSetup} */
  const out = { pricing: 'weight', pricePerKgCentavos: ppk.ok ? ppk.centavos : 0, stockUnit: d.stockUnit, prepOptions };
  if (d.sizeMode === 'bands') {
    const hasDefault = d.bands.some((b) => b.isDefault);
    out.bands = d.bands.map((b, i) => ({ minG: intOf(b.minG), maxG: intOf(b.maxG), isDefault: hasDefault ? b.isDefault : i === 0 }));
  } else {
    out.targetMinG = intOf(d.targetMinG);
    out.targetMaxG = intOf(d.targetMaxG);
    out.targetStepG = intOf(d.targetStepG);
  }
  return out;
}

/** @param {ProductDraft} d @returns {import('@popcart/core/v1').GrillProductSetup} */
export function buildGrillSetup(d) {
  const groupCodes = new Set(d.groups.map((g) => g.code).filter(Boolean));
  const modifierGroups = d.groups.map((g) => {
    const code = g.code || makeCode(g.name, groupCodes, { digits: true });
    groupCodes.add(code);
    const optCodes = new Set(g.options.map((o) => o.code).filter(Boolean));
    return {
      code,
      name: g.name.trim(),
      minChoices: intOf(g.min),
      maxChoices: intOf(g.max),
      options: g.options.map((o) => {
        const oc = o.code || makeCode(o.label, optCodes, { digits: true });
        optCodes.add(oc);
        return { code: oc, label: o.label.trim(), priceCentavos: pesosOr0(o.price), isDefault: o.isDefault };
      }),
    };
  });
  return {
    menuSection: opt(d.menuSection),
    modifierGroups,
    comboComponents: d.combo.map((c) => ({ productId: c.productId, qty: intOf(c.qty) })),
  };
}

/**
 * Everything the form edits, as ProductInput (create adds visible + initialStock).
 * Mart: variantOf on create only; variantLabel and mart.pack are always sent on edit (so they can
 * be cleared) and on create only when set.
 * @param {ProductDraft} d @param {{ isCreate: boolean }} o
 * @returns {import('./mart.js').MartProductInput}
 */
export function buildInput(d, o) {
  /** @type {import('./mart.js').MartProductInput} */
  const input = {
    name: d.name.trim(),
    line: d.line,
    pricing: d.pricing,
    priceCentavos: d.pricing === 'weight' ? 0 : pesosOr0(d.price),
    unitLabel: opt(d.unitLabel),
    description: opt(d.description),
    categoryId: d.categoryId || null,
    source: opt(d.source),
    sort: d.sort.trim() === '' ? 0 : intOf(d.sort),
  };
  if (d.line === 'grill') {
    input.prepMinutes = intOf(d.prepMinutes);
    input.grill = buildGrillSetup(d);
  }
  if (d.line === 'mart') {
    input.barcode = opt(d.barcode);
    input.shelf = opt(d.shelf);
    if (o.isCreate && d.variantOf) input.variantOf = d.variantOf;
    if (!o.isCreate || opt(d.variantLabel)) input.variantLabel = opt(d.variantLabel);
    if (!o.isCreate || d.pack.length) input.mart = { pack: packPayload(d.pack) };
  }
  if (d.line === 'fresh') input.fresh = buildFreshSetup(d);
  if (o.isCreate) {
    input.visible = d.visible;
    const s = parseStockAmount(d.initialStock, stockUnitOf(d));
    input.initialStock = s.ok ? s.value : 0;
  }
  return input;
}

const PATCH_SKIP = new Set(['line', 'visible', 'initialStock', 'variantOf']);

/**
 * Only what changed, so the audit trail shows real edits. The line is fixed after create;
 * visibility has its own endpoint. Line setups (fresh/grill) are sent whole when any part moved.
 * @param {ProductDraft} d @param {ProductDraft} original the draft built from the loaded product
 * @returns {Partial<import('./mart.js').MartProductInput>}
 */
export function buildPatch(d, original) {
  const next = /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (buildInput(d, { isCreate: false })));
  const prev = /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (buildInput(original, { isCreate: false })));
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const [k, v] of Object.entries(next)) {
    if (PATCH_SKIP.has(k)) continue;
    if (JSON.stringify(v) !== JSON.stringify(prev[k])) out[k] = v;
  }
  // Pricing travels with its setup: the server checks them together.
  if (out.fresh && !('pricing' in out)) out.pricing = next.pricing;
  return /** @type {Partial<import('./mart.js').MartProductInput>} */ (out);
}

/** @param {ProductDraft} d @param {ProductDraft} original */
export function isDirty(d, original) {
  return Object.keys(buildPatch(d, original)).length > 0;
}

/**
 * What the customer card will show for a weight product: the default band's (or the smallest
 * target's) estimate, the same maths as the server's cardEstimate. null until it can be worked out.
 * @param {ProductDraft} d
 */
export function cardEstimatePreview(d) {
  if (d.line !== 'fresh' || d.pricing !== 'weight') return null;
  const ppk = parsePesos(d.pricePerKg, { max: MAX_PPK_CENTAVOS });
  if (!ppk.ok || ppk.centavos <= 0) return null;
  let grams = NaN;
  if (d.sizeMode === 'bands') {
    const b = d.bands.find((x) => x.isDefault) || d.bands[0];
    if (b && gramsOk(intOf(b.minG)) && gramsOk(intOf(b.maxG)) && intOf(b.maxG) >= intOf(b.minG))
      grams = bandEstimateGrams({ minG: intOf(b.minG), maxG: intOf(b.maxG) });
  } else grams = intOf(d.targetMinG);
  if (!gramsOk(grams)) return null;
  return { grams, centavos: priceForGrams(grams, ppk.centavos) };
}

// ── Server errors → form fields ───────────────────────────────────────────────────────────

/** Server field (ProductInput / setup key) → the form field that shows the message. */
export const SERVER_FIELD = {
  name: 'name',
  line: 'line',
  pricing: 'pricing',
  priceCentavos: 'price',
  unitLabel: 'unitLabel',
  description: 'description',
  categoryId: 'categoryId',
  category: 'categoryId',
  source: 'source',
  sort: 'sort',
  prepMinutes: 'prepMinutes',
  barcode: 'barcode',
  shelf: 'shelf',
  variantLabel: 'variantLabel',
  variantOf: 'variantLabel',
  pack: 'pack',
  mart: 'pack',
  initialStock: 'initialStock',
  pricePerKgCentavos: 'pricePerKg',
  stockUnit: 'stockUnit',
  bands: 'bands',
  targetMinG: 'target',
  targetMaxG: 'target',
  targetStepG: 'target',
  prepOptions: 'prepOptions',
  menuSection: 'menuSection',
  modifierGroups: 'groups',
  comboComponents: 'combo',
  image: 'photo',
};

/**
 * Put a server error on the field it names: "fresh.bands[1].minG" → bands, "grill.modifierGroups" →
 * groups. Anything unrecognised goes to the form-level message.
 * @param {{ status?: number; field?: string; message?: string } | null | undefined} err
 * @returns {{ field: string; message: string } | null}
 */
export function mapServerError(err) {
  if (!err) return null;
  const message = err.message || 'Couldn’t save. Try again.';
  if (err.status !== 400 && err.status !== 409) return { field: 'form', message };
  const parts = String(err.field || '')
    .split(/[.[\]]+/)
    .filter((s) => s && !/^\d+$/.test(s));
  for (const p of parts) {
    const f = /** @type {Record<string, string>} */ (SERVER_FIELD)[p];
    if (f) return { field: f, message };
  }
  if (parts.includes('fresh')) return { field: 'pricePerKg', message };
  if (parts.includes('grill')) return { field: 'groups', message };
  return { field: 'form', message };
}

/** Fields in form order, for focusing the first error. */
export const FIELD_ORDER = [
  'name', 'line', 'pricing', 'price', 'unitLabel', 'categoryId', 'description', 'source', 'sort', 'initialStock',
  'pricePerKg', 'stockUnit', 'bands', 'target', 'prepOptions',
  'prepMinutes', 'menuSection', 'groups', 'combo',
  'barcode', 'shelf', 'variantLabel', 'pack', 'photo', 'form',
];

/** @param {Record<string, string>} errors */
export function firstError(errors) {
  return FIELD_ORDER.find((f) => errors[f]) ?? Object.keys(errors)[0] ?? null;
}

// ── List editing helpers (groups, options, bands, prep, combo) ────────────────────────────

/**
 * Move an item up (-1) or down (+1); out-of-range moves return the same array.
 * @template T @param {T[]} xs @param {number} i @param {-1 | 1} dir
 */
export function move(xs, i, dir) {
  const j = i + dir;
  if (i < 0 || i >= xs.length || j < 0 || j >= xs.length) return xs;
  const out = xs.slice();
  const a = /** @type {T} */ (out[i]);
  out[i] = /** @type {T} */ (out[j]);
  out[j] = a;
  return out;
}

/** @returns {GroupDraft} */
export function newGroup() {
  return { key: newKey(), code: '', name: '', min: '1', max: '1', options: [newOption()] };
}
/** @returns {OptionDraft} */
export function newOption() {
  return { key: newKey(), code: '', label: '', price: '', isDefault: false };
}

/**
 * Pre-pick an option. A single-choice group keeps one default; multi-choice groups toggle.
 * @param {GroupDraft} g @param {string} optionKey
 * @returns {GroupDraft}
 */
export function toggleDefault(g, optionKey) {
  const single = intOf(g.max) === 1;
  return {
    ...g,
    options: g.options.map((o) => {
      if (o.key === optionKey) return { ...o, isDefault: !o.isDefault };
      return single ? { ...o, isDefault: false } : o;
    }),
  };
}

/** Make one band the default (exactly one). @param {BandDraft[]} bands @param {string} key */
export function setDefaultBand(bands, key) {
  return bands.map((b) => ({ ...b, isDefault: b.key === key }));
}

/**
 * Add a product to the combo (or raise its quantity when it's already in).
 * @param {ComboDraft[]} combo @param {{ id: string; name: string }} p
 */
export function addComboItem(combo, p) {
  const at = combo.findIndex((c) => c.productId === p.id);
  if (at >= 0) return combo.map((c, i) => (i === at ? { ...c, qty: String((intOf(c.qty) || 0) + 1) } : c));
  return [...combo, { productId: p.id, name: p.name, qty: '1' }];
}

/**
 * Products that can go into a combo: same shop, not archived, not combos, not itself, not already in.
 * @param {import('@popcart/core/v1').AdminProduct[]} products @param {ComboDraft[]} combo @param {string | null} selfId
 */
export function comboCandidates(products, combo, selfId) {
  const inCombo = new Set(combo.map((c) => c.productId));
  return products.filter((p) => p.status !== 'archived' && !p.isCombo && p.id !== selfId && !inCombo.has(p.id));
}

/** "Pick 1 (required)", "Pick up to 3 (optional)", "Pick 2 to 3". @param {Pick<GroupDraft, 'min' | 'max'>} g */
export function choiceRule(g) {
  const min = intOf(g.min);
  const max = intOf(g.max);
  if (!Number.isInteger(min) || !Number.isInteger(max)) return '';
  if (min === 0) return max === 1 ? 'Optional · pick up to 1' : `Optional · pick up to ${max}`;
  if (min === max) return `Required · pick ${min}`;
  return `Required · pick ${min} to ${max}`;
}
